import React, {useMemo} from 'react';
import {Stack, Typography, Button, Box, Grid2} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useOutletContext } from 'react-router-dom';
import { ReportSearch } from "./report-search";
import { toast } from 'react-toastify';
import { useGenericMutation } from "../../../../services/react-query/generic.mutation";
import {ReportTypes, UserTypes} from "../../../../types";
import { When } from "react-if";
import {NotifyUser} from "./notify-user";
import ReportsBackIcon from "../../../../assets/icons/reports-back-icon";
import {ReportResult} from "./report-result";
import {generatePDF} from "../../../../helpers/downloadPdf";
import {styled} from "@mui/material/styles";
import PdfLetterHead from "../../../../components/pdf-download-letterhead";
import dayjs from 'dayjs';

const PdfDownloadDiv = styled('div')(({ theme }) => ({
    '.div-border-for-download': {border:'1px solid #ddd'},
    table: {borderCollapse:'collapse'},
    '.text-download-adjust':{
        'margin-top' : '-20px !important'
    },
    '.MuiSlider-thumb::before':{
        'margin-top' : '-15px !important'
    }
}));

export const SafetyReport: React.FC = () => {

    interface ContextType {
        setReportTitle: (title: string) => void;
        setReportDescText: (desc: string) => void;
    }

    interface Address {
        address: string;
        placeId: string;
    }

    const SCREENS = {
        ADDRESS_INPUT: 0,
        REPORT_RESULT: 1,
        NOTIFY_USER: 2
    }
    const { setReportTitle, setReportDescText } = useOutletContext<ContextType>();
    useMemo(() => {
        setReportTitle("Safety Reports");
        setReportDescText("Searching a location will return results for crime and sex offender information at the address provided");
    }, []);

    const navigate = useNavigate();
    const [address, setAddress] = React.useState<Address>({address:'', placeId:''});
    const [resultData, setResultData] = React.useState<{}>({});
    const [screenCount, setScreenCount] = React.useState<number>(SCREENS.ADDRESS_INPUT);
    const [alreadyFetchedAddress, setAlreadyFetchedAddress] = React.useState<Address | null>(null);
    const [crimeScore, setCrimeScore] = React.useState<number>(0);
    const [sexOffenders, setSexOffenders] = React.useState<
        ReportTypes.SexOffender[]
        >([]);
    const [nearbyReports, setNearbyReports] = React.useState<
        ReportTypes.NearbyReport[]
        >([]);
    const [sexOffendersWithin3M, setSexOffendersWithin3M] = React.useState<
        ReportTypes.SexOffender[]
        >([]);

    const [notifyDetailsModalOpen, setNotifyDetailsModalOpen] = React.useState<boolean>(false);

    const saveReports = (selectedUsers : UserTypes.Model[], isNotify : boolean) => {
        let sexOffenderAtLocation : ReportTypes.SexOffender  = sexOffenders[0]
        let requestObject: ReportTypes.ReportData = {
            place : {
                ...address
            },
            reportType : 'Safety Report',
            reportComment : "No sex offender at location found",
            address : address.address,
            crimeScore
        }
        let notifiedUsersId : string[] = [];

        if(sexOffenderAtLocation && Object.keys(sexOffenderAtLocation).length){
            requestObject.place.location = {
                latitude : sexOffenderAtLocation.sex_offender_lat,
                longitude : sexOffenderAtLocation.sex_offender_lon
            }
            requestObject.offenderName = sexOffenderAtLocation.sex_offender_name
            requestObject.reportComment = `${sexOffenderAtLocation.sex_offender_name}, ${sexOffenderAtLocation?.sex_offender_charges}`
            requestObject.sexOffender = {
                sexOffenderNearByCount : sexOffendersWithin3M.length,
                sexOffendersAtLocation : sexOffenders
            }
            requestObject.latitude = sexOffenderAtLocation.sex_offender_lat
            requestObject.longitude = sexOffenderAtLocation.sex_offender_lon
        }
        if(isNotify && selectedUsers.length){
            selectedUsers.map((user)=>{
               notifiedUsersId.push(user.id);
            });
            requestObject.notifiedUsersId = notifiedUsersId;
        }

        let toastMessage = "Report saved";
        saveReport(
            {reportData : requestObject},
            {
                onSuccess: (data) => {
                    console.log("data", data);
                    onSuccessCallbacks(data);
                    if(isNotify){
                        toastMessage = "Users are notified about the report";
                    }
                    navigate("/reports/field-safety-reports");
                    toast(toastMessage, {
                        autoClose: 1500,
                        type: 'success',
                    });
                },
                onError: (error) => {
                    console.log("Error",error)
                    toast("Error occurred. Please check server logs", {
                        autoClose: 1500,
                        type: 'error',
                    });
                },

            })
    }

    const { mutate: getCrimeReports, isLoading } =
        useGenericMutation<
            Address,
            ReportTypes.CrimeData
            >('getCrimeReports', 'reports/getCrimeReportsByLocation', 'post');

    const { mutate: saveReport, isLoading : isSaveReportLoading } =
        useGenericMutation<
            {reportData:ReportTypes.ReportData},
            ReportTypes.CrimeData
            >('saveReport', 'reports/saveReport', 'post');

    const handleSearch = () => {
        if(alreadyFetchedAddress?.address === address.address && alreadyFetchedAddress?.placeId === address.placeId){
            onSuccessCallbacks(resultData);
            return
        }
        getCrimeReports(
            address,
            {
                onSuccess: (data) => {
                    console.log("data", data);
                    onSuccessCallbacks(data);
                    setSexOffenders(data.result.sexOffenderAtLocation);
                    setSexOffendersWithin3M(data.result.sexOffenderNearby);
                    setCrimeScore(data.result.crimeScore);
                    setNearbyReports(data.result.nearbyReports);
                },
                onError: (error) => {
                    console.log("Error",error)
                    toast("Error occurred. Please check server logs", {
                        autoClose: 1500,
                        type: 'error',
                    });
                }
            }
        );
    };

    const onSuccessCallbacks = (data:any) => {
        setResultData(data);
        setScreenCount(SCREENS.REPORT_RESULT);
        setAlreadyFetchedAddress(address);
    }

    const handleBack = () => {
        if(screenCount === SCREENS.ADDRESS_INPUT){
            navigate("/reports/")
        }else{
            setScreenCount(screenCount-1);
        }
    }

    const handleNext = () => {
        setScreenCount(screenCount+1);
    }

    const handleDownload = async () => {
        const fileName = address.address.replaceAll(" ","_")+'__'+dayjs(new Date()).format('MM_DD_YYYY__hh:mm_A')
        
        const componentJsx = <PdfLetterHead>
            <PdfDownloadDiv>
                <Grid2 container>
                    <Grid2 size={{md:6,xs:12,sm:12}}>
                        <ReportResult
                            nearbyReports={nearbyReports}
                            address={address.address}
                            crimeScore={crimeScore}
                            sexOffenders={sexOffenders}
                            sexOffendersWithin3M={sexOffendersWithin3M?.length}
                            isPastReport={true}
                            reportType={'Safety Report'}
                            isDownloadIconVisible={false}
                        />
                    </Grid2>
                </Grid2>
            </PdfDownloadDiv>
        </PdfLetterHead>
        
        const {downloadPDF} = generatePDF();

        await downloadPDF(componentJsx, {
            fileName,
            margins: 1,
            scale: 2,
            stretch : 1.4,
            containerWidth : '1600px',
            orientation: 'portrait'
        });

        toast('Report downloaded successfully', {
            autoClose: 1500,
            type: 'success',
        });
    }

    return (
        <Stack>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'left',
                textAlign: 'left',
                marginTop: 4,
                marginBottom: 6
            }}>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleBack}
                    disabled={false}
                    sx={{
                        border : 0,
                        padding:1,
                        boxShadow : '0 0 2px 0 rgba(145, 158, 171, 0.24),0 16px 32px -4px rgba(145, 158, 171, 0.24)'
                    }}
                >
                    <ReportsBackIcon/>
                    <Typography fontWeight={400} fontSize={14} color={'black'} marginLeft={1}>
                        Back
                    </Typography>
                </Button>
            </Box>
            <When condition={screenCount === SCREENS.ADDRESS_INPUT}>
                <ReportSearch
                    address={address}
                    isLoading={isLoading}
                    handleSearch={handleSearch}
                    setAddress={setAddress}
                    disabled={!address.address && !address.placeId}
                />
            </When>
            <When condition={screenCount === SCREENS.REPORT_RESULT}>
                <Grid2 container>
                    <Grid2 size={{md:6,xs:12,sm:12}}>
                        <ReportResult
                            nearbyReports = {nearbyReports}
                            address={address.address}
                            crimeScore={crimeScore}
                            sexOffenders={sexOffenders}
                            sexOffendersWithin3M={sexOffendersWithin3M?.length}
                            handleNext={handleNext}
                            handleDownload={handleDownload}
                            reportType={'Safety Report'}
                        />
                    </Grid2>
                </Grid2>
            </When>
            <When condition={screenCount === SCREENS.NOTIFY_USER}>
                <NotifyUser
                    notifyDetailsModalOpen={notifyDetailsModalOpen}
                    address={address}
                    setNotifyDetailsModalOpen={setNotifyDetailsModalOpen}
                    isSaveReportLoading ={isSaveReportLoading}
                    saveReports = {saveReports}
                />
            </When>
        </Stack>

    )
}
